import type { AwsRegion } from './Aws.types';
import type { IConfigV2DeployableStacks } from './Cdk.types';
import type { CloudEnvironment } from './CloudEnvironment';
import type { DomainFragments } from './Http.types';
import type { PackageJson } from './Node.types';

/**
 * Minimal interface for reading package information from the repository.
 * Used by ServiceReader to avoid requiring full IConfigV2 (which needs API access).
 */
export interface IPackageProvider {
  getServices(): ConfigV2Package[];
  getServiceSdks(): ConfigV2Package[];
}

/** Result type for getS3BucketForPackage method. */
export interface S3BucketForPackageResult {
  bucketConfig: ConfigV2GlobalResourcesS3Bucket;
  bucketRef: string;
}

export interface IConfigV2 extends IPackageProvider {
  getAllPackages(): ConfigV2Package[];
  getApps(): ConfigV2Package[];
  getConfig(): ConfigV2FileStructure;
  getConfigFilePath(): string;
  getConfigMappings(): IConfigV2Mappings;
  getDatabaseTypeForDatabaseRef(rdsRef: string): ConfigV2DatabaseType;
  getDefaultBehaviorBucketName(): string;
  getDeployableStacks(): IConfigV2DeployableStacks;
  getDomainForApiGatewayRef(apiGatewayRef: string, environment: CloudEnvironment): DomainFragments;
  getDomains(): string[];
  getDynamoDbSdks(): ConfigV2Package[];
  getElectronApp(packageName: string): ConfigV2ElectronApp | undefined;
  getEnvironmentConfig(environment: CloudEnvironment): ConfigV2EnvironmentObject | undefined;
  getEnvironments(): CloudEnvironment[];
  getHostedZoneId(domain: string): string;
  getLambdaBuildsBucketName(): string;
  getLogBucketName(): string;
  getManifestBuildsBucketName(): string;
  getMobileApp(packageName: string): ConfigV2MobileApp | undefined;
  getModules(): ConfigV2Package[];
  getMysqlSdks(): ConfigV2Package[];
  getPackageNameFromPackage(pkgName: string): string;
  getPostgresSdks(): ConfigV2Package[];
  getProjectId(): string | undefined;
  getPublishablePackages(): ConfigV2Package[];
  getRepositoryPath(): string;
  getS3BucketForPackage(packageName: string): S3BucketForPackageResult | undefined;
  getServices(): ConfigV2Package[];
  getServiceSdks(): ConfigV2Package[];
  getSites(): ConfigV2Package[];
  getStaticSiteForEnvironment(
    environment: CloudEnvironment,
    siteRef: string,
    packageName?: string
  ): ConfigV2StaticSite | undefined;
  getStaticSitesForEnvironment(environment: CloudEnvironment): ConfigV2StaticSite[];
  getStorybookS3Ref(): string | undefined;
}

export interface ConfigV2FileStructure {
  awsAccount: ConfigV2AwsAccount; // Only list valid versions here.
  devDeployment?: ConfigV2DevDeployment;
  electronApps?: ConfigV2ElectronApps;
  environments?: {
    [CloudEnvironment.DEV]?: ConfigV2EnvironmentObject;
    [CloudEnvironment.STG]?: ConfigV2EnvironmentObject;
    [CloudEnvironment.PROD]?: ConfigV2EnvironmentObject;
  };
  globalResources?: ConfigV2GlobalResources;
  mobileApps?: ConfigV2MobileApps;
  project: ConfigV2Project;
  version: 2;
}

export interface ConfigV2Project {
  name: string;
}

export interface ConfigV2AwsAccount {
  accountNumber: string;
  devDeploymentDomain?: string;
  domains?: { [domain: string]: string };
  regions?: AwsRegion[];
  s3?: ConfigV2AwsAccountS3;
  sshKeys?: { [ref: string]: string };
  stsAssumeRoleArns?: string[];
  vpc?: { [ref: string]: string };
}

export interface ConfigV2AwsAccountS3 {
  defaultBehavior: string;
  lambdaBuilds: string;
  logBucket: string;
  manifestBuilds: string;
}

export interface ConfigV2GlobalResources {
  codeBuildDeployer?: { [ref: string]: ConfigV2GlobalResourcesCodeBuildDeployer };
  cognito?: { [ref: string]: ConfigV2GlobalResourcesCognitoUserPool };
  dynamoDb?: { [ref: string]: ConfigV2GlobalResourcesDynamoDb };
  grafana?: { [ref: string]: ConfigV2GlobalResourcesGrafanaInstance };
  grafanaAgent?: { [ref: string]: ConfigV2GlobalResourcesGrafanaAgent };
  iotPresence?: ConfigV2GlobalResourcesIoTPresence;
  mysql?: { [ref: string]: ConfigV2GlobalResourcesMysqlInstance };
  postgresServerlessV2?: { [ref: string]: ConfigV2GlobalResourcesPostgresInstance };
  s3?: { [ref: string]: ConfigV2GlobalResourcesS3Bucket };
  secret?: { [ref: string]: ConfigV2GlobalResourcesSecret };
}

/**
 * The remote deployment engine (design §5.3): a single CodeBuild project that installs `@blufin/cli` and runs
 * `hnl aws run-request` against a claimed Deployment Request. Everything here becomes plain build environment
 * except the two secrets named by `m2mSecretRef`/`npmTokenSecretRef`, which are wired as CodeBuild
 * `SECRETS_MANAGER`-type environment variables built from the ARNs their deployed `Secret` stacks output — the
 * values are resolved by CodeBuild at build start and never enter the synthesized template.
 */
export interface ConfigV2GlobalResourcesCodeBuildDeployer {
  authUrl: string; // Auth service base URL the CLI authenticates against (BLUFIN_AUTH_URL).
  awsInfraUrl: string; // aws-infra service base URL the CLI reports Run/stack status to (BLUFIN_AWS_INFRA_URL).
  cognitoAppName: string; // The MACHINE (client_credentials) app client under `cognitoRef` whose clientId/clientSecret pair `m2mSecretRef` holds.
  cognitoRef: string; // The `cognito` entry whose DEPLOYED hosted domain supplies BLUFIN_M2M_TOKEN_URL.
  iotMessagingRef: string; // The `iotMessaging` entry whose deployed IoT endpoint + topicPrefix the build publishes live logs through.
  m2mSecretRef: string; // The `secret` entry (kind JSON, keys `clientId`/`clientSecret`) holding the M2M CLI identity.
  npmTokenSecretRef: string; // The `secret` entry (kind STRING) holding an npm token with `@blufin` read scope.
  projectKey: string; // The project identity the checkout-free CLI runs as (BLUFIN_PROJECT_KEY).
  removalPolicy?: 'DESTROY' | 'RETAIN'; // Defaults to DESTROY if not specified. Note the log-archive bucket is ALWAYS retained.
}

export interface ConfigV2CognitoSes {
  domain: string;
  fromEmail: string;
  fromName?: string;
  replyTo?: string;
}

/**
 * A HUMAN app client — the default. Signs a person in via the `adminUserPassword` flow, redirects
 * through `callbackUrls`/`logoutUrls`, and holds a refresh token. Resource-server scopes are
 * meaningless here; they belong to a MACHINE client.
 */
export interface ConfigV2CognitoAppHuman {
  callbackUrls?: string[];
  clientType?: ConfigV2CognitoClientType.HUMAN; // Omit for the default. Present only so a HUMAN app can be declared explicitly.
  logoutUrls?: string[];
  mfaIssuer?: string; // Authenticator-app label for TOTP enrollment (eg: 'HnlSoftware - Admin'). Falls back to a default when omitted.
  name: string;
}

/**
 * A MACHINE (M2M) app client. Mints its own access token via the OAuth `client_credentials` grant, so
 * it has no browser redirect, no TOTP enrollment and no refresh token — none of the HUMAN fields apply.
 * The pool must define `resourceServers`: Cognito requires such a client to hold at least one scope.
 */
export interface ConfigV2CognitoAppMachine {
  clientType: ConfigV2CognitoClientType.MACHINE;
  name: string;
  scopes?: string[]; // Fully-qualified `<identifier>/<scope>` grants, eg: ['blufin-api/cli']. When omitted, the app receives every scope the pool's resourceServers define.
}

export type ConfigV2CognitoApp = ConfigV2CognitoAppHuman | ConfigV2CognitoAppMachine;

export interface ConfigV2CognitoResourceServer {
  identifier: string; // OAuth2.0 resource-server identifier (eg: 'blufin-api'). Scopes are cited by MACHINE apps as `<identifier>/<scope>`.
  scopes: string[]; // Scope names exposed by this resource server (eg: ['cli']).
}

export interface ConfigV2GlobalResourcesCognitoUserPool {
  apps: ConfigV2CognitoApp[];
  customEmailSender?: boolean; // Defaults to false if not specified.
  customSmsSender?: boolean; // Defaults to false if not specified.
  mfa?: 'OFF' | 'OPTIONAL' | 'REQUIRED'; // Defaults to OFF (no MFA) if not specified.
  removalPolicy?: 'DESTROY' | 'RETAIN'; // Defaults to DESTROY if not specified.
  resourceServers?: ConfigV2CognitoResourceServer[]; // OAuth2.0 resource servers exposing scopes for MACHINE (M2M) app clients. Required when any app sets clientType: MACHINE.
  ses?: ConfigV2CognitoSes;
}

export interface ConfigV2GlobalResourcesDynamoDb {
  removalPolicy?: 'DESTROY' | 'RETAIN'; // Defaults to DESTROY if not specified.
  schema: string; // S3 path/ref, eg: @blufin/dynamodb/AdminPermissions
  /**
   * Opt-in stream consumer Lambda deployed alongside the table by DynamoDbStackV2. Requires the table's schema to
   * declare `streamViewType: 'OLD_IMAGE'`. Omit for a table with no stream consumer (the default).
   */
  streamConsumer?: ConfigV2DynamoDbStreamConsumer;
  version: string; // Version of the schema, eg: 20250514-0001
}

export interface ConfigV2GlobalResourcesGrafanaStream {
  deadLetterRetentionDays: number; // Lifecycle (days) for the S3 dead-letter bucket holding failed Firehose deliveries.
  metrics: Record<string, string[]>; // CloudWatch namespace (eg AWS/Lambda) -> metric-name include-list for the us-west-2 Metric Stream.
  metricsUsEast1?: Record<string, string[]>; // us-east-1 metrics-only include-list — AWS/CloudFront only (global metrics live in us-east-1).
}

export interface ConfigV2GlobalResourcesGrafanaInstance {
  adminEmail: string;
  allowedIpv4: string[];
  cognitoAppName: string;
  cognitoRef: string;
  ebsVolumeSize?: number; // Default 50GB if not specified.
  ec2InstanceType: ConfigV2Ec2InstanceType;
  fqdn: string; // eg: grafana.blufin.org — self-contained, NOT derived from environment cloudfront config.
  sshKeyRef: string;
  stream?: ConfigV2GlobalResourcesGrafanaStream; // CloudWatch Metric Stream / Firehose telemetry include-list (design §5.1).
  vpcRef: string;
}

export interface ConfigV2GlobalResourcesGrafanaAgent {
  grafanaRef: string; // References which grafana instance to send data to (eg: 'Main').
  logGroupPattern?: string; // Advanced/testing override: if set, used verbatim as the single logs subscription pattern (bypasses per-env derivation).
  metricsLogGroupPattern?: string; // Advanced/testing override: if set, used verbatim as the single metrics subscription pattern (bypasses per-env derivation).
  telemetryEnvironments?: CloudEnvironment[]; // Builder-injected (NOT set in YAML): the deployed envs whose telemetry log groups this global agent subscribes to. Derived from config.environments by DeployableStacksBuilder.
  vpcRef: string; // VPC for lambda-promtail/lambda-mimirPush placement.
}

export interface ConfigV2GlobalResourcesRdsInstance {
  allowedIpv4: string[];
  removalPolicy?: 'DESTROY' | 'RETAIN'; // Defaults to DESTROY if not specified.
  schemas?: { [schemaRef: string]: string }; // eg: { SchemaRef1: 'SchemaDefinition', SchemaRef2: 'SchemaDefinition' }
  vpcRef: string;
}

export interface ConfigV2GlobalResourcesMysqlInstance extends ConfigV2GlobalResourcesRdsInstance {
  ec2InstanceType: ConfigV2Ec2InstanceType;
}

export interface ConfigV2GlobalResourcesPostgresInstance extends ConfigV2GlobalResourcesRdsInstance {}

export interface ConfigV2S3LifecycleRule {
  expireAfterDays: number;
  tag: { key: string; value: string };
}

export interface ConfigV2GlobalResourcesS3Bucket {
  /** For FILE_REGISTRY buckets: origins permitted to issue browser-direct presigned PUTs. */
  allowedOrigins?: string[];
  /** For FILE_REGISTRY buckets: the dynamoDb ref naming the AssetTable the processor writes to. */
  dynamoDbRef?: string;
  /**
   * BLOB only. Objects carrying the tag expire `expireAfterDays` after creation; untagged objects are never touched.
   */
  lifecycleRules?: ConfigV2S3LifecycleRule[];
  /** List of package names that deploy to this bucket. Required for SPA and ELECTRON types. */
  packages?: string[];
  /**
   * DESTROY (the default) empties and deletes the bucket when its stack is torn down; RETAIN leaves the bucket and
   * every object in place. Bucket names are DETERMINISTIC (`AwsS3Bucket.getBucketName`: hyphenated id + a hash of the
   * construct id, no CloudFormation random suffix), so a RETAIN'd bucket outlives its stack under the exact name the
   * next create will ask for — re-creating that stack then fails on the name collision until someone manually deletes
   * the orphan. Use RETAIN only where losing the objects is worse than that.
   */
  removalPolicy?: 'DESTROY' | 'RETAIN';
  type: ConfigV2GlobalResourcesS3BucketType;
}

/**
 * A human-supplied secret in the registry. CDK creates an EMPTY shell (no version at all) and a human populates it
 * once via `hnl secret upload` — a value NEVER appears in this config, in a synthesized template, or in a build log.
 * Modelled as a discriminated union on `kind` rather than an optional `keys` with a runtime check: a STRING secret
 * has no keys to declare, and the schema's `oneOf` makes a STRING entry carrying `keys` an outright error.
 */
export type ConfigV2GlobalResourcesSecret =
  | { description?: string; keys: string[]; kind: ConfigV2GlobalResourcesSecretKind.JSON }
  | { description?: string; kind: ConfigV2GlobalResourcesSecretKind.STRING };

export interface ConfigV2GlobalResourcesIoTPresence {
  /**
   * Shared MQTT-over-WebSocket hostname per environment (eg: `{ prod: 'mqtt.blufin.org' }`). Each key must be
   * a {@link CloudEnvironment}; each value must resolve to a registered domain. ONE certificate covers every
   * value — AWS IoT permits one server certificate per parent domain — which is why every planned hostname is
   * listed here from day one, including environments that do not exist yet: ACM cannot amend an issued
   * certificate, so adding a name later would replace it and, with it, the already-live domain configuration.
   * An entry is RESERVED (certificate name only, no endpoint) until its environment declares at least one
   * `iotMessaging` entry; only then does it get a domain configuration and a CNAME.
   */
  mqttCustomDomains?: Partial<Record<CloudEnvironment, string>>;
  removalPolicy?: 'DESTROY' | 'RETAIN';
}

export interface ConfigV2EnvironmentIoTMessaging {
  allowedOrigins?: string[]; // CORS: List of allowed origins (eg: https://atlas.blufin.org)
  cognitoAppName: string;
  cognitoRef: string;
  /**
   * Custom FQDN for the presence HTTP API (API Gateway v2) ONLY — it is fronted by an API Gateway v2 custom
   * domain and becomes an A/alias record. It does NOT front the AWS IoT Core data endpoint: that hostname is
   * account-wide and is named by `globalResources.iotPresence.mqttCustomDomains`, never per entry.
   */
  customDomain?: string;
  removalPolicy?: 'DESTROY' | 'RETAIN';
  schema: string;
  throttling: { burstLimit: number; rateLimit: number };
  topicPrefix: string;
  version: string;
}

export interface ConfigV2EnvironmentObject {
  apiGateway?: { [ref: string]: ConfigV2EnvironmentApiGateway };
  cloudfront?: { [fqdn: string]: { [path: string]: ConfigV2EnvironmentCloudfront } };
  iotMessaging?: { [ref: string]: ConfigV2EnvironmentIoTMessaging };
}

/**
 * Settings for a single endpoint's Lambda function configuration.
 * Used in endpointSettings to configure memoryInMb and timeoutInSeconds per endpoint.
 */
export interface ConfigV2EndpointSettings {
  /** Lambda memory allocation in MB. Range: 128-10240. */
  memoryInMb?: number;
  /**
   * Per-endpoint API Gateway throttle override. Falls back to `_default` then the service-level `throttling`
   * when omitted. Both `burstLimit` and `rateLimit` must be positive (enforced by the config validator).
   */
  throttling?: { burstLimit: number; rateLimit: number };
  /** Lambda timeout in seconds. Range: 1-900. */
  timeoutInSeconds?: number;
}

/** Rate unit for a `rate` schedule expression (interval-based scheduling). */
export enum ConfigV2ScheduleRateUnit {
  DAYS = 'DAYS',
  HOURS = 'HOURS',
  MINUTES = 'MINUTES'
}

/** Cron fields for a `cron` schedule expression (calendar-based scheduling, full AWS cron syntax per field). */
export interface ConfigV2ScheduleCron {
  dayOfMonth?: string;
  dayOfWeek?: string;
  hour?: string;
  minute?: string;
  month?: string;
  year?: string;
}

/**
 * A schedule expression — a discriminated union of `rate` (intervals) vs `cron` (calendar). Never a raw
 * AWS expression string. Exactly one of `rate`/`cron` must be present (enforced at validation).
 */
export type ConfigV2ScheduleExpression =
  | { rate: { unit: ConfigV2ScheduleRateUnit; value: number } }
  | { cron: ConfigV2ScheduleCron };

/** A single scheduled handler's per-environment trigger configuration (keyed by the handler's base name). */
export interface ConfigV2ScheduledHandler {
  enabled?: boolean; // default true; false → schedule created but disabled
  expression: ConfigV2ScheduleExpression;
  input?: Record<string, unknown>; // optional static JSON passed to the handler as `payload`
  memoryInMb?: number;
  timeoutInSeconds?: number;
  timezone?: string; // IANA tz, eg: 'America/Vancouver' (only valid with `cron`)
}

/** A single queued consumer's optional per-environment tuning (keyed by the consumer's base name). */
export interface ConfigV2QueuedHandler {
  enabled?: boolean; // default true; false → event source mapping disabled, messages accumulate
  memoryInMb?: number;
  timeoutInSeconds?: number;
}

export interface ConfigV2EnvironmentApiGateway {
  allowedOrigins?: string[]; // CORS: List of allowed origins (eg: https://atlas.blufin.org)
  cognitoRef?: string;
  customDomain?: string;
  dynamoDb?: { [connectionRef: string]: string };
  /**
   * Per-endpoint Lambda configuration overrides.
   * Keys are endpoint export names (eg: 'authenticateUserAlpha') or '_default' for all endpoints.
   * Values specify memoryInMb and/or timeoutInSeconds.
   */
  endpointSettings?: { [endpointNameOrDefault: string]: ConfigV2EndpointSettings };
  /**
   * Public, human-readable spam-deterrent label (eg: `hmac-authservicestaff-prod-x7k2m9`) — NOT a secret. At CDK synth
   * time it is deterministically hashed into the opaque `BF_HMAC_SECRET` token handed to browsers via `GET /v1/hmac`.
   * Format: `hmac-<ref-lowercased>-<env>-<suffix>`. Required at synth for any HMAC-authenticated service.
   */
  hmacToken?: string;
  iotMessaging?: { [connectionRef: string]: string }; // connectionRef → iotMessaging resource ref
  mysql?: { [connectionRef: string]: ConfigV2DatabaseConnectionRef };
  package: string;
  postgresServerlessV2?: { [connectionRef: string]: ConfigV2DatabaseConnectionRef };
  queues?: { [queuedHandlerName: string]: ConfigV2QueuedHandler }; // keyed by consumer base name; optional tuning only
  schedules?: { [scheduledHandlerName: string]: ConfigV2ScheduledHandler }; // EventBridge Scheduler triggers, keyed by handler base name
  target?: ConfigV2EnvironmentApiGatewayTarget;
  throttling: { burstLimit: number; rateLimit: number };
  version: string;
}

/** Used by the shared ApiGateway validator. */
export interface ConfigV2EnvironmentApiGatewayMinimal {
  cognitoRef?: string;
  dynamoDb?: { [connectionRef: string]: string }; // String == databaseRef
  iotMessaging?: { [connectionRef: string]: string }; // connectionRef → iotMessaging resource ref
  mysql?: { [connectionRef: string]: ConfigV2DatabaseConnectionRef };
  postgresServerlessV2?: { [connectionRef: string]: ConfigV2DatabaseConnectionRef };
  target?: ConfigV2EnvironmentApiGatewayTarget;
}

export interface ConfigV2EnvironmentApiGatewayTarget {
  cloudfrontDistributionRef?: string; // FQDN of the CloudFront distribution to grant invalidation on + surface via ctx
  cognitoRef?: string;
  /**
   * Access to a DynamoDB table owned by a Cognito stack (eg: { AuthTable: 'Staff' }). Maps a connectionRef →
   * the cognitoRef whose stack owns the table. Unlike `dynamoDb` (standalone tables, read-write), these grant
   * point/index reads only by DEFAULT (`dynamodb:GetItem`/`BatchGetItem`/`Query` — deliberately NO `Scan`).
   * The single exception: a package + endpoint + connectionRef triple listed in `CognitoDynamoDbWriters`
   * (`modules/aws-cdk/src/helpers/CognitoDynamoDbWriters/CognitoDynamoDbWriters.ts`) ADDITIONALLY receives
   * `PutItem`/`UpdateItem`. `DeleteItem`, `BatchWriteItem` and `ConditionCheckItem` are never granted here.
   */
  cognitoDynamoDb?: { [connectionRef: string]: string };
  grafanaRef?: string;
  /**
   * s3Ref of the bucket this service is granted access to. A FILE_REGISTRY ref grants the asset pipeline's
   * three scoped statements (PutObject on `incoming/*`, DeleteObject on `pub/uploads/*`, prefix-conditioned
   * ListBucket) on a bucket the service SHARES with the processor Lambda and the `S3FileRegistry` construct's
   * own grants; a BLOB ref grants whole-bucket Get/Put/DeleteObject plus unconditioned ListBucket on a bucket
   * the service owns outright.
   */
  s3Bucket?: string;
}

export interface ConfigV2DatabaseMap {
  dynamoDb: string[]; // eg: [ DynamoDb1, DynamoDb2 ]
  mysql: string[]; // eg: [ Mysql1, Mysql2 ]
  postgresServerlessV2: string[]; // eg: [ Postgres1, Postgres2 ]
}

export interface ConfigV2DatabaseConnectionRef {
  databaseRef: string;
  schemaRef: string;
}

export interface ConfigV2EnvironmentCloudfront {
  canaryDeployment?: ConfigV2EnvironmentCloudfrontCanaryDeployment[];
  origin: ConfigV2CloudfrontOriginType;
  originPath?: string;
  originRef?: string;
  package?: string;
  redirectTo?: string;
}

export interface ConfigV2EnvironmentCloudfrontCanaryDeployment {
  percentOfTraffic: number;
  version: string;
}

export interface ConfigV2FilePackage {
  name: string;
  type: ConfigV2PackageType;
}

export interface ConfigV2Package extends ConfigV2FilePackage {
  packageJson?: PackageJson; // Contents of package.json file, eg: /Users/albert/Repos/blufin/services/auth/package.json
  packageJsonPath: string; // Full path to the package.
  packageName: string;
  path: string; // Full path to the service root, eg: /Users/albert/Repos/blufin/services/auth
  pathRelative: string; // Relative path to the service root from repo root, eg: services/auth
  version: string;
}

export interface ConfigV2DevDeployment {
  services: ConfigV2DevDeploymentServices;
}

export interface ConfigV2DevDeploymentServices {
  domain: string;
  packages: { [packageName: string]: ConfigV2DevDeploymentService };
}

export interface ConfigV2DevDeploymentService {
  cognitoRef?: string;
  dynamoDb?: { [connectionRef: string]: string }; // String == databaseRef
  mysql?: { [connectionRef: string]: ConfigV2DatabaseConnectionRef };
  postgresServerlessV2?: { [connectionRef: string]: ConfigV2DatabaseConnectionRef };
  target?: ConfigV2EnvironmentApiGatewayTarget;
}

export interface ConfigV2ElectronApp {
  /**
   * Reverse-DNS bundle identifier, WITHOUT any environment suffix — the CLI appends
   * `.dev` / `.stg` / `.local` and leaves prod bare. Never contains a version.
   */
  bundleId: string;
}

export interface ConfigV2ElectronApps {
  [packageName: string]: ConfigV2ElectronApp;
}

export interface ConfigV2MobileApp {
  /**
   * Reverse-DNS bundle identifier, used VERBATIM as `ios.bundleIdentifier` for every environment — the
   * mobile build appends no `.dev`/`.stg`/`.local` suffix, deliberately diverging from Electron (design §8).
   */
  bundleId: string;
}

export interface ConfigV2MobileApps {
  [packageName: string]: ConfigV2MobileApp;
}

export interface ConfigV2StaticSite {
  cloudfront: ConfigV2EnvironmentCloudfront;
  domain: string;
  subdomain: string;
}

/** ---- Config Mappings! ---- */

export interface IConfigV2Mappings {
  apiGatewayRefs: {
    environment: Record<CloudEnvironment, { [apiGatewayRef: string]: { cloudfrontDomain?: string } }>;
  };
  cognitoRefs: string[]; // eg: [ DevMaster ]
  domains: string[];
  dynamoDbRefs: IConfigV2MappingsDynamoDbRefs;
  grafanaRefs: {
    [grafanaRef: string]: IConfigV2MappingsGrafana;
  };
  iotMessagingRefs: { [environment: string]: string[] };
  packages: { [pkgName: string]: { type: ConfigV2PackageType } };
  rdsRefs: IConfigV2MappingsRdsRefs;
  s3Refs: {
    electronApps: string[];
    fileRegistries: string[];
    spaApps: string[];
    staticSites: string[];
    storybooks: string[];
  };
  sshKeyRefs: string[];
  usages: IConfigV2MappingsUsages;
  vpcRefs: string[];
}

export interface IConfigV2MappingsDynamoDbRefs {
  [dynamoRef: string]: string; // eg: { AuthService: '@blufin/dynamodb/AuthSchema' }
}

export interface IConfigV2MappingsGrafana {
  cognitoRef: string;
  sshKeyRef: string;
}

export interface IConfigV2MappingsRdsRefs {
  mysql: IConfigV2MappingsRdsSchemaRefs;
  postgresServerlessV2: IConfigV2MappingsRdsSchemaRefs;
}

export interface IConfigV2MappingsRdsSchemaRefs {
  [rdsRef: string]: { [schemaRef: string]: string };
}

export interface IConfigV2MappingsUsages {
  fqdn: { [fqdn: string]: string[] }; // eg: { blufin.org: [ global.cloudFront ] }
  serviceVersions: { [packageName: string]: string[] }; // eg: { @blufin/auth-service: [ 1.2.3, 1.2.4 ] }
}

/** ---- Enums ---- */

export enum ConfigV2DatabaseType {
  DYNAMO_DB = 'DYNAMO_DB',
  MYSQL = 'MYSQL',
  POSTGRES_SERVERLESS_V2 = 'POSTGRES_SERVERLESS_V2'
}

export enum ConfigV2Ec2InstanceType {
  MEDIUM = 'MEDIUM',
  MICRO = 'MICRO',
  SMALL = 'SMALL'
}

export enum ConfigV2PackageType {
  APP_ELECTRON_BUNDLED = 'app-electron-bundled',
  APP_ELECTRON_WEB = 'app-electron-web',
  APP_REACT = 'app-react',
  APP_REACT_NATIVE = 'app-react-native',
  MODULE_BROWSER = 'module-browser',
  MODULE_NODE = 'module-node',
  /** A published Node executable (a package with a `bin`): CJS-only, no ESM bundle, no `module` field. */
  MODULE_NODE_BIN = 'module-node-bin',
  MODULE_REACT_NATIVE = 'module-react-native',
  MODULE_UI = 'module-ui',
  PLUGIN = 'plugin',
  SDK_DYNAMODB = 'sdk-dynamodb',
  SDK_IOT_CLIENT = 'sdk-iot-client',
  SDK_IOT_SERVER = 'sdk-iot-server',
  SDK_MYSQL = 'sdk-mysql',
  SDK_POSTGRES = 'sdk-postgres',
  SDK_SERVICE = 'sdk-service',
  SERVICE = 'service',
  SITE_NEXTJS = 'site-nextjs'
}

/**
 * The stream-consumer Lambdas `DynamoDbStackV2` knows how to deploy beside a table. Each value is bound to the
 * ONE table schema whose item shapes its handler hardcodes — `ConfigV2Validator` enforces that binding, because a
 * consumer attached to the wrong table would silently write nonsense keys.
 */
export enum ConfigV2DynamoDbStreamConsumer {
  /** Releases the quota reservation a TTL-expired PENDING/SCANNING asset still held (`@blufin/dynamodb/AssetTable`). */
  TTL_QUOTA_REAPER = 'TTL_QUOTA_REAPER'
}

/**
 * Which kind of Cognito app client a `globalResources.cognito.[REF].apps` entry provisions. The two are
 * mutually exclusive shapes, not a shared shape with a flag: a HUMAN client carries redirect URLs and an
 * MFA issuer, a MACHINE client carries resource-server scopes, and neither one's fields mean anything on
 * the other. `AwsCognitoUserPool` branches on this to synthesize two entirely different `UserPoolClient`s.
 */
export enum ConfigV2CognitoClientType {
  /** Person signing in (adminUserPassword flow, callback/logout URLs, refresh token). The default. */
  HUMAN = 'HUMAN',
  /** Machine-to-machine (OAuth client_credentials grant, resource-server scopes, no refresh token). */
  MACHINE = 'MACHINE'
}

export enum ConfigV2GlobalResourcesS3BucketType {
  /**
   * Private object storage owned by exactly one service: encrypted, no public access, no website, no CDN.
   * Like every other bucket type it defaults to `RemovalPolicy.DESTROY` + `autoDeleteObjects: true` (destroying
   * the S3 stack empties and deletes the bucket with its contents) unless the entry sets `removalPolicy: RETAIN`.
   */
  BLOB = 'BLOB',
  ELECTRON = 'ELECTRON',
  FILE_REGISTRY = 'FILE_REGISTRY',
  SPA = 'SPA',
  STATIC_SITE = 'STATIC_SITE',
  STORYBOOK = 'STORYBOOK'
}

/**
 * The shape a registry secret's value takes once a human uploads it. Values are UPPERCASE here (unlike
 * `AwsSecretKind`'s lowercase members) because they are written verbatim into `config-v2.yml`, where every
 * other enum-valued key is uppercase.
 */
export enum ConfigV2GlobalResourcesSecretKind {
  /** A `{...}` envelope whose keys are declared up-front (eg: `clientId` + `clientSecret`). */
  JSON = 'JSON',
  /** An opaque single value stored as the ENTIRE SecretString (eg: an npm token). */
  STRING = 'STRING'
}

/**
 * The two JSON keys the deployment engine's M2M credential pair is made of, and the ONE place they are named.
 * Three sites depend on them and TWO different questions are asked of them — the asymmetry is deliberate, so do
 * NOT "fix" the two comparisons into agreement:
 * - `validateCodeBuildDeployers` and `CodeBuildDeployerStackV2` ask "are both declared?" (a SUBSET check). The
 *   CodeBuild `<arn>:<key>` selector reads exactly these two and is unaffected by any further key the registry
 *   entry also declares, so a superset is a perfectly valid config.
 * - `hnl secret upload --from-cognito` asks "are these the only two?" (an EXACT-SET check). Cognito's stack
 *   outputs can fill only these two, so populating a superset entry from Cognito would write an envelope missing
 *   a declared key — a defect that surfaces at the consumer days later. The flag refuses; the masked prompt and
 *   `--stdin` still populate such an entry.
 */
export const COGNITO_M2M_SECRET_KEYS = ['clientId', 'clientSecret'] as const;

export enum ConfigV2CloudfrontOriginType {
  API_GATEWAY = 'API_GATEWAY',
  REDIRECT = 'REDIRECT',
  S3 = 'S3'
}
